import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { actionPromocionesGet } from "../../redux/actions/promociones/promociones";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const isVigente = (p) => {
  const hoy = dayjs().format("YYYY-MM-DD");
  return p.fecha_inicio <= hoy && p.fecha_fin >= hoy;
};

export default function PromocionesPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.promociones);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionPromocionesGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    vigentes: items.filter(isVigente).length,
    vencidas: items.filter((p) => !isVigente(p)).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "vigentes") return items.filter(isVigente);
    if (activeFilter === "vencidas") return items.filter((p) => !isVigente(p));
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de promociones", icon: <i className="fa-solid fa-tag" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "vigentes", label: "Vigentes", icon: <i className="fa-solid fa-circle-check" />, iconCls: "paq-stat-icon-decoracion", count: counts.vigentes },
    { key: "vencidas", label: "Vencidas", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-barra", count: counts.vencidas },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Promociones</Title>
                <Text className="paq-subtitle">Catalogo de promociones vigentes y pasadas.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre de promocion..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Promociones ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} promociones encontradas</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/promociones/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nueva promocion
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin promociones que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((p) => {
                        const vigente = isVigente(p);
                        return (
                          <div key={p.id_promocion} className="paq-card">
                            <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                              <i className="fa-solid fa-tag paq-card-banner-icon" />
                            </div>

                            <div className="paq-card-header">
                              <div className="paq-card-head">
                                <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{p.nombre}</Paragraph>
                              </div>
                            </div>

                            <div className="paq-card-body">
                              <div className="paq-contenidos-box">
                                <i className="fa-solid fa-calendar paq-info-icon" />
                                <div className="paq-info-text">
                                  <span className="paq-info-label">Vigencia</span>
                                  <span className="paq-info-value">{p.fecha_inicio} al {p.fecha_fin}</span>
                                </div>
                              </div>
                            </div>

                            <div className="paq-card-footer">
                              <div className={`paq-bottom-strip ${vigente ? "is-green" : "is-red"}`}>
                                <span className="paq-strip-label">{vigente ? "Vigente" : "Vencida"}</span>
                              </div>
                              <Button
                                className="paq-btn-details"
                                icon={<i className="fa-solid fa-circle-info" />}
                                onClick={() => navigate(`/app/promociones/${p.id_promocion}`)}
                              >
                                Ver detalles
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
